/**
 * Saves, then waits for the re-read of the saved record before resolving, so a
 * caller's next save cannot be overtaken by this older read. Resolves false
 * when the save fails, leaving the caller to report it. A failed re-read is
 * not a failed save: it resolves true, and `onRereadFailed` stands the saved
 * values in for the read that did not come.
 */
export const saveThenReread = async (
  save: () => Promise<unknown>,
  afterSave: () => void,
  reread: () => Promise<unknown>,
  onRereadFailed?: () => void,
): Promise<boolean> => {
  try {
    await save();
  } catch {
    return false;
  }
  afterSave();
  try {
    await reread();
  } catch (error) {
    console.error("Saved, but could not re-read the record:", error);
    onRereadFailed?.();
  }
  return true;
};

/**
 * Runs each task only after the one before it settles, so saves and their
 * re-reads land in the order they were asked for, even when they overlap.
 */
export const serialQueue = () => {
  let last: Promise<unknown> = Promise.resolve();
  return <T>(task: () => Promise<T>): Promise<T> => {
    const run = last.then(task);
    last = run.catch(() => undefined);
    return run;
  };
};
